import {
  ChangeDetectionStrategy,
  Component,
  HostListener,
  Input,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import { AuthService } from '../../../services/auth/auth.service';
import { faArrowLeft } from '@fortawesome/free-solid-svg-icons/faArrowLeft';
import { NotifierService } from 'angular-notifier';
import { TranslateService } from '@ngx-translate/core';
import { HeaderMenuItem } from '../../../interfaces/header-links';
import { filter, tap } from 'rxjs/operators';
import { ConfirmationDialogComponent } from '../../modals/confirm-dialog/confirmation-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { Router, UrlTree } from '@angular/router';
import { IRestaurantInfo } from '../../../interfaces/restaurant';
import { AdminService } from '../../../services/admin/admin.service';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { faArrowRight } from '@fortawesome/free-solid-svg-icons';
import { faSignOutAlt } from '@fortawesome/free-solid-svg-icons/faSignOutAlt';
import { UserRoles } from '../../../shared/enums/userRoles';

@UntilDestroy()
@Component({
  selector: 'app-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class HeaderComponent implements OnChanges{
  @Input() links: HeaderMenuItem[] = [];
  @Input() backLink: UrlTree | string | any[] | null = null;
  @Input() restaurant: IRestaurantInfo | null = null;
  @Input() chainName: string | null = null;

  public restaurants!: IRestaurantInfo[];
  public prevRestaurant!: IRestaurantInfo | null;
  public nextRestaurant!: IRestaurantInfo | null;
  public icons = {
    back: faArrowLeft,
    next: faArrowRight,
    logout: faSignOutAlt,
  };

  constructor(
    private translate: TranslateService,
    private notifier: NotifierService,
    private matDialog: MatDialog,
    private adminService: AdminService,
    public auth: AuthService,
    private router: Router,
  ) {}

  @HostListener('window:keydown', ['$event'])
  handleKeyboardEvent(event: KeyboardEvent): void {
    if (event.key === 'ArrowLeft' && this.prevRestaurant?.id) {
      this.router.navigate(['/restaurants', this.prevRestaurant.id, 'tables']);
    } else if (event.key === 'ArrowRight' && this.nextRestaurant?.id) {
      this.router.navigate(['/restaurants', this.nextRestaurant.id, 'tables']);
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.restaurant && this.restaurant) {
      this.updateRestaurants();
    }
  }

  private updateRestaurants(): void {
    this.adminService.filteredRestaurants$.pipe(
      tap((restaurants: IRestaurantInfo[]) => {
        this.restaurants = restaurants;

        const currentIndex = restaurants.findIndex(r => r.id === this.restaurant?.id);

        if (this.restaurant?.meta?.userRole !== UserRoles.waiter && this.restaurant?.meta?.userRole !== UserRoles.admin) {
          this.prevRestaurant = currentIndex > 0 ? restaurants[currentIndex - 1] : null;
          this.nextRestaurant = currentIndex >= 0 && currentIndex < restaurants.length - 1 ? restaurants[currentIndex
          + 1] : null;
        }
      }),
      untilDestroyed(this)
    ).subscribe();
  }

  public confirmToLogout(): void {
    const title = this.translate.instant('login.logoutConfirm.title');
    const message = this.translate.instant('login.logoutConfirm.message');

    const confirmationModal = this.matDialog.open(ConfirmationDialogComponent, {
      data: {
        message,
        title
      },
      panelClass: 'confirmation-modal'
    });

    confirmationModal.afterClosed().pipe(
      filter(Boolean),
    ).subscribe((): void => {
      this.logout();
    });
  }

  private logout(): void{
    this.auth.logout().subscribe(() => {
      this.notifier.notify('success', this.translate.instant('login.messages.logout'));
    });
  }

 public back(): void {
    if (Array.isArray(this.backLink)) {
      this.router.navigate(this.backLink);
    } else if (this.backLink) {
      this.router.navigateByUrl(this.backLink);
    }
  }
}
